import React, { useState, useEffect, useCallback, useRef } from "react";
import { ProgressBar } from "react-bootstrap";
import Sound from "react-sound";
import { s3base } from "api.js";
import NoMicrophone from "views/NoMicrophone.jsx";
import PlaybackSpeedBtns from "components/PracticeMaterial/PlaybackSpeedBtns.jsx";
import Beep from "Beep.js";

import "components/custom.css";
import MicWaveform from "../MicWaveform";
import RecordedWaveform from "components/Question/Speaking/RecordedWaveform";
import SelectVoiceDropDown from "components/PracticeMaterial/SelectVoiceDropDown.jsx";
import ReusableRecorder from "components/Recorder/ReusableRecorder";
import IOSAudioPlayer from "./IOSAudioPlayer.jsx";
import { isIOSDevice } from 'util/deviceDetection';
import useAudioDuration from "hooks/useAudioDuration";

const RepeatSentece = (props) => {
  const [isRecording, setIsRecording] = useState(false);
  const [blobURL, setBlobURL] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [question, setQuestion] = useState("");
  const [secondCount, setSecondCount] = useState(3);
  const [duration, setDuration] = useState(0);
  const [secondCount2, setSecondCount2] = useState(1);
  const [recordCount, setRecordCount] = useState(0);
  const [recordId, setRecordId] = useState(1);
  const [intervalId, setIntervalId] = useState(0);
  const [intervalId2, setIntervalId2] = useState(0);
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [soundStatus, setSoundStatus] = useState(true);
  const [soundPer, setSoundPer] = useState(0);
  const [trans, setTrans] = useState("");
  const [volume, setVolume] = useState(90);
  const [correctFlag, setCorrectFlag] = useState(true);
  const [beepStatus, setBeepStatus] = useState(false);
  const [position, setPosition] = useState(0);
  const [file, setFile] = useState("");
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [soundKey, setSoundKey] = useState(0);
  const [secondCount2Bckup, setSecondCount2Bckup] = useState(0);
  const [saveTranscript, setSaveTranscriptState] = useState(false);
  const [micPermitted, setMicPermitted] = useState(true);
  const [browserAllowed, setBrowserAllowed] = useState(true);
  const [audioFinished, setAudioFinished] = useState(false);
  const [questionAudio, setQuestionAudio] = useState(null);
  const [defaultSelectedVoice, setDefaultSelectedVoice] = useState(null);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(null);
  const [audioRetryCount, setAudioRetryCount] = useState(0);
  const [mediaStreamReady, setMediaStreamReady] = useState(false);
   const { audioDuration, audioSecondsLeft, handleAudioPlaying, resetAudioDuration } = useAudioDuration();

   // --- Mobile specific states ---
  const [mobileStarted, setMobileStarted] = useState(false);
  const [showMobileStartButton, setShowMobileStartButton] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const audioContextRef = useRef(null);
  const unlockedAudioRef = useRef(null); // pre-unlocked Audio element for iOS autoplay
  const handleRecordingCompleteRef = useRef(null);
  const { currentQuestion, total_questions } = props;
  const LIMIT = 10;  

  // Refs to store mutable values and prevent dependency issues
  const recorderRef = useRef(null);
  const mediaStreamRef = useRef(null); // ✅ Store MediaStream
  const intervalsRef = useRef({
    intervalId: 0,
    intervalId2: 0,
    recordId: 0
  });

  // Store latest values in refs to avoid stale closures
  const latestValues = useRef({
    isBlocked,
    isRecording,
    secondCount,
    secondCount2,
    secondCount2Bckup,
    recordCount,
    soundStatus,
    trans,
    file,
    duration,
    blobURL
  });

  // Update refs when values change
  useEffect(() => {
    latestValues.current = {
      isBlocked,
      isRecording,
      secondCount,
      secondCount2,
      secondCount2Bckup,
      recordCount,
      soundStatus,
      trans,
      file,
      duration,
      blobURL
    };
  });

  // ============================================
  // CRITICAL FIX: Acquire MediaStream ONCE on mount
  // ============================================
  useEffect(() => {

    if (isIOSDevice()) {
      setShowMobileStartButton(true);
      setBrowserAllowed(true);
      return;
    }

    let isSubscribed = true;

    setBrowserAllowed(true);

    // ✅ Request mic permission ONCE, early (on mount)
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (isSubscribed) {
          mediaStreamRef.current = stream; // ✅ Store the stream
          setMicPermitted(true);
          setIsBlocked(false);
          setMediaStreamReady(true);

          // Pass stream to recorder immediately
          if (recorderRef.current && recorderRef.current.setMediaStream) {
            recorderRef.current.setMediaStream(stream);
          }
        }
      })
      .catch((err) => {
        console.error('Microphone permission denied:', err);
        if (isSubscribed) {
          setMicPermitted(false);
          setIsBlocked(true);
        }
      });

    // Cleanup: Release MediaStream on unmount
    return () => {
      isSubscribed = false;
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      // Clear all intervals
      if (intervalsRef.current.intervalId) {
        clearInterval(intervalsRef.current.intervalId);
      }
      if (intervalsRef.current.intervalId2) {
        clearInterval(intervalsRef.current.intervalId2);
      }
      if (intervalsRef.current.recordId) {
        clearInterval(intervalsRef.current.recordId);
      }
    };
  }, [mobileStarted]);

    // ============================================
  // Start recording with existing MediaStream (iOS compatible)
  // ============================================
  const startRecordingWithExistingStream = useCallback(() => {
    if (!mediaStreamRef.current) {
      console.error('MediaStream not ready');
      return;
    }

    if (!recorderRef.current) {
      console.error('Recorder not initialized');
      return;
    }

    console.log('🎙️ [RepeatSentece] Starting recording with pre-acquired stream (iOS compatible)');

    // ✅ Start recording (no new getUserMedia needed)
    recorderRef.current.startRecording();
    setIsRecording(true);
    setBlobURL("");

    // Start recording timer
    const newRecordId = setInterval(() => {
      setRecordCount(prevRecordCount => {
        if (prevRecordCount === (LIMIT)) {
          clearInterval(intervalsRef.current.recordId);
          intervalsRef.current.recordId = 0;
          if (recorderRef.current) {
            recorderRef.current.stopRecording();
          }
          return prevRecordCount;
        }
        return prevRecordCount + 1;
      });
    }, 1000);

    intervalsRef.current.recordId = newRecordId;
    setRecordId(newRecordId);
    setSoundStatus(false);
  }, []);

  const startRecordingNow = useCallback(() => {
    if (latestValues.current.isBlocked) return;

    if (recorderRef.current) {
      if (isIOSDevice() && audioContextRef.current) {
        audioContextRef.current.resume().then(() => {
          recorderRef.current.startRecording();
        }).catch(() => {
          recorderRef.current.startRecording();
        });
      } else {
        recorderRef.current.startRecording();
      }
    }

    setIsRecording(true);
    setBlobURL("");
    setRecordCount(0);

    if (intervalsRef.current.recordId) {
      clearInterval(intervalsRef.current.recordId);
    }

    const newRecordId = setInterval(() => {
      setRecordCount(prevRecordCount => {
        if (prevRecordCount === LIMIT) {
          clearInterval(intervalsRef.current.recordId);
          intervalsRef.current.recordId = 0;
          if (recorderRef.current) {
            recorderRef.current.stopRecording();
          }
          return prevRecordCount;
        }
        return prevRecordCount + 1;
      });
    }, 1000);

    intervalsRef.current.recordId = newRecordId;
    setRecordId(newRecordId);
  }, [isIOSDevice]);

  const handleBeepFinishedPlaying = useCallback(() => {
    // Recording already started when beep began — just clear beep status
    setBeepStatus(false);
  }, []);


  // Timer functions that don't depend on state
  const timer = useCallback(() => {
    setSecondCount(prevSecondCount => {
      if (prevSecondCount === 0) {
        clearInterval(intervalsRef.current.intervalId);
        intervalsRef.current.intervalId = 0;
        return prevSecondCount;
      } else {
        return prevSecondCount - 1;
      }
    });
  }, []);

  const startProcess = useCallback(() => {
    if (!props.answer && props.data?.media_link) {
      const newIntervalId = setInterval(timer, 1000);
      intervalsRef.current.intervalId = newIntervalId;
      setIntervalId(newIntervalId);

      const audio_url = s3base + props.data.media_link;
      props.updateQuestionAudioUrlInPractice?.(audio_url);
    }
  }, [props.answer, props.data?.media_link, props.updateQuestionAudioUrlInPractice, timer]);

  const handleMobileStart = useCallback(async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      console.log('Mobile Start button clicked');

      // For iOS, prepare AudioContext
      if (isIOSDevice()) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          audioContextRef.current = new AudioContext();
          if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          // Warm up audio system
          const oscillator = audioContextRef.current.createOscillator();
          const gainNode = audioContextRef.current.createGain();
          gainNode.gain.setValueAtTime(0.001, audioContextRef.current.currentTime);
          oscillator.connect(gainNode);
          gainNode.connect(audioContextRef.current.destination);
          oscillator.start();
          oscillator.stop(audioContextRef.current.currentTime + 0.1);
        }
      }

      // Unlock a reusable Audio element for iOS autoplay.
      // Calling play() synchronously inside this user-gesture handler permanently
      // unlocks this Audio object so it can play freely later (e.g. in canplaythrough).
      if (!unlockedAudioRef.current) {
        const ua = new Audio();
        // Minimal silent WAV so iOS accepts the play() call
        ua.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        const unlockPromise = ua.play();
        if (unlockPromise !== undefined) {
          unlockPromise
            .then(() => { ua.pause(); ua.currentTime = 0; })
            .catch(() => {}); // a rejected play() still partially unlocks on most iOS versions
        }
        unlockedAudioRef.current = ua;
      }

      // Request microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setMicPermitted(true);
      setIsBlocked(false);

      // ✅ Immediately pass the stream to the recorder
      if (recorderRef.current && recorderRef.current.setMediaStream) {
        recorderRef.current.setMediaStream(stream);
      }

      setMediaStreamReady(true);
      setMobileStarted(true);
      setShowMobileStartButton(false);
      // No need to call startProcess() here – it will be triggered by the useEffect
    } catch (error) {
      console.error('Error in mobile start:', error);
      setMicPermitted(false);
      setIsBlocked(true);
    } finally {
      setIsStarting(false);
    }
  }, [isStarting, isIOSDevice]);
    // ============================================
  // Mobile beep handling (replaces Sound beep on mobile)
  // ============================================
  const createAndPlayBeep = useCallback((audioContext) => {
    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      setTimeout(() => {
        setBeepStatus(false);
      }, 300);
    } catch (error) {
      console.error('Error playing beep sound:', error);
      setBeepStatus(false);
    }
  }, [handleBeepFinishedPlaying]);

  const playMobileBeep = useCallback(() => {
    if (isIOSDevice()) {
      // iOS needs explicit AudioContext resume
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        let audioContext = audioContextRef.current;
        if (!audioContext) {
          audioContext = new AudioContext();
          audioContextRef.current = audioContext;
        }
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            createAndPlayBeep(audioContext);
          }).catch(err => {
            console.error('Failed to resume AudioContext:', err);
            setBeepStatus(false);
          });
        } else {
          createAndPlayBeep(audioContext);
        }
      } catch (error) {
        console.error('Mobile beep error:', error);
        setTimeout(() => {
          setBeepStatus(false);
        }, 300);
      }
    } else {
      // Android – try AudioContext, fallback to Sound if needed
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        let audioContext = audioContextRef.current;
        if (!audioContext) {
          audioContext = new AudioContext();
          audioContextRef.current = audioContext;
        }
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            createAndPlayBeep(audioContext);
          }).catch(() => {
            createAndPlayBeep(audioContext); // try anyway
          });
        } else {
          createAndPlayBeep(audioContext);
        }
      } catch (error) {
        console.error('Android beep error, falling back to Sound', error);
        setBeepStatus(true); // fallback to Sound component
      }
    }
  }, [createAndPlayBeep, handleBeepFinishedPlaying, isIOSDevice]);

  const selectAndTryAudio = useCallback((index) => {
    const { question_audios } = props.data;
    if (question_audios && question_audios.length > 0 && index < question_audios.length) {
      const selected = question_audios[index];
      setQuestionAudio(`${s3base}/${selected.value}`);
      setDefaultSelectedVoice(selected.label);
      setCurrentAudioIndex(index);
    }
  }, [props.data]);

  const handleAudioError = useCallback((errorCode, description) => {
  console.log("Got the error", errorCode, description);

    const { question_audios, media_link } = props.data;

  // No alternate audios → fallback immediately
    if (!question_audios || question_audios.length === 0) {
      console.error("No alternative audio available, using media_link");
      setQuestionAudio(`${s3base}${media_link}`);
      setCurrentAudioIndex(null);
      setSoundKey(prev => prev + 1);
      return;
    }

  // All alternates exhausted → fallback
    if (audioRetryCount >= question_audios.length) {
      console.error("All question_audios failed, using media_link");
      setQuestionAudio(`${s3base}${media_link}`);
      setCurrentAudioIndex(null);
      setAudioRetryCount(0);
      setSoundKey(prev => prev + 1);
      return;
    }

  // ✅ Normalize index to avoid NaN
  const safeCurrentIndex =
    Number.isInteger(currentAudioIndex) ? currentAudioIndex : -1;

    const nextIndex = (safeCurrentIndex + 1) % question_audios.length;

    setAudioRetryCount(prev => prev + 1);
    setSoundKey(prev => prev + 1);

    setTimeout(() => {
      selectAndTryAudio(nextIndex);
    }, 100);
  }, [
  props.data,
  currentAudioIndex,
  audioRetryCount,
  selectAndTryAudio
  ]);

  const getQuestionAudio = useCallback((data) => {
    if (!data) return { question_audio_local: null, defaultSelectedVoice_local: null };

    let question_audio_local = null;
    let defaultSelectedVoice_local = null;

    if ("question_audios" in data && data.question_audios?.length > 0) {
      const x = Math.floor(Math.random() * Math.min(6, data.question_audios.length));
      const selected = data.question_audios[x];
      question_audio_local = `${s3base}/${selected.value}`;
      defaultSelectedVoice_local = selected.label;
      setCurrentAudioIndex(x);
      setAudioRetryCount(0);
    } else if (data.media_link) {
      question_audio_local = `${s3base}${data.media_link}`;
    }

    return { question_audio_local, defaultSelectedVoice_local };
  }, []);

  useEffect(() => {
    if (!props.data) return;

    const currentId = props.data.id;

    // New question or restart
    if (!questionId || questionId !== currentId || props.restart) {
    // Reset everything
      if (intervalsRef.current.intervalId)
        clearInterval(intervalsRef.current.intervalId);
      if (intervalsRef.current.intervalId2)
        clearInterval(intervalsRef.current.intervalId2);
      if (intervalsRef.current.recordId)
        clearInterval(intervalsRef.current.recordId);

      const { question_audio_local, defaultSelectedVoice_local } =
        getQuestionAudio(props.data);

      setQuestionAudio(question_audio_local);
      setDefaultSelectedVoice(defaultSelectedVoice_local);
      setQuestionId(currentId);
      setCorrectFlag(true);
      setRecordCount(0);
      setSoundPer(0);
      setIsRecording(false);
    setSecondCount(3); // ← or 8? decide
      setSecondCount2(10);
      setSecondCount2Bckup(0);
      setBlobURL("");
      setTrans("");
      setDuration(0);
      setSoundStatus(true);
      setAudioFinished(false);
      setSoundKey((prev) => prev + 1);

    // Optional: notify parent
      if (question_audio_local && props.updateQuestionAudioUrlInPractice) {
        props.updateQuestionAudioUrlInPractice(question_audio_local);
      }
    }
  }, [props.data?.id, props.restart, getQuestionAudio]);

  const timer1 = useCallback(() => {
    setSecondCount2(prevSecondCount2 => {
      if (prevSecondCount2 === 0) {
        clearInterval(intervalsRef.current.intervalId2);
        intervalsRef.current.intervalId2 = 0;
        // Start recording immediately when beep plays so no words are missed
        startRecordingNow();
        if (isIOSDevice()) {
          playMobileBeep();
        } else {
          setBeepStatus(true);
        }
        setSoundStatus(false);
        return prevSecondCount2;
      } else {
        if (latestValues.current.secondCount2Bckup === 0) {
          setSecondCount2Bckup(prevSecondCount2);
        }
        return prevSecondCount2 - 1;
      }
    });
  }, []);

  useEffect(() => {
    if (micPermitted && browserAllowed && mediaStreamReady) {
      startProcess();
    }
  }, [micPermitted, browserAllowed, mediaStreamReady, startProcess]);

  useEffect(() => {
    if (props.restart) {
      props.restartQue?.();
    }
  }, [props.restart, props.restartQue]);

  // Ensure MediaStream is passed to recorder when both are ready
  useEffect(() => {
    if (mediaStreamRef.current && recorderRef.current && recorderRef.current.setMediaStream) {
      console.log('🔗 [RepeatSentece] Linking MediaStream to recorder');
      recorderRef.current.setMediaStream(mediaStreamRef.current);
    }
  }, [mediaStreamReady]);

  const stop = useCallback(async () => {
    if (!props.testMode) {
      props.setIsStopped?.(false);
    }
    if (!latestValues.current.isBlocked && latestValues.current.isRecording && recorderRef.current) {
      recorderRef.current.stopRecording();
    }
  }, [props.testMode]);

  const handleSongPlaying = useCallback((obj) => {
    const p = (obj.position * 100 / obj.duration);
    setSoundPer(p);
    handleAudioPlaying(obj);
  }, []);

  const handleSongFinishedPlaying = useCallback(() => {
    setSoundStatus(false);
   if(props.onMainAudioFinished){
      props.onMainAudioFinished();
    }
    setAudioFinished(true);
    props.soundChecked?.();
  }, [props.soundChecked]);

  const stopAudio = useCallback(() => {
    setSoundStatus(false);
   if(props.onMainAudioFinished){
      props.onMainAudioFinished();
    }
    setSecondCount2(10);
     resetAudioDuration();
  }, []);

  const skip = useCallback(() => {
    if (intervalsRef.current.intervalId) {
      clearInterval(intervalsRef.current.intervalId);
      intervalsRef.current.intervalId = 0;
    }
    setSecondCount(0);
  }, []);

  const changeRange = useCallback((e) => {
    setVolume(parseInt(e.target.value));
  }, []);

  const loadingSound = useCallback((obj) => {
    let d = Math.max(0, obj.duration / 1000 - 0.3);
    if (obj.duration !== 0 && intervalsRef.current.intervalId2 === 0) {
      const newIntervalId = setInterval(timer1, 1000);
      intervalsRef.current.intervalId2 = newIntervalId;
      setSoundStatus(true);
      setIntervalId2(newIntervalId);
      setSecondCount2(Math.ceil(d));
      setSecondCount2Bckup(Math.ceil(d));
    }
  }, [timer1]);

  const changePlaybackSpeed = useCallback((speed) => {
  // Don't allow speed changes if audio has finished
    if (audioFinished) return;

  console.log(latestValues.current.secondCount2Bckup);
    if (speed !== playbackSpeed) {
      let seconds = latestValues.current.secondCount2Bckup;
      if (latestValues.current.secondCount === 0) {
        if (speed === 2 || speed === 1.5) {
          seconds = seconds / speed;
        }
      }
      setSoundKey(prevSoundKey => prevSoundKey + 1);
      setPlaybackSpeed(speed);
      setSecondCount2(Math.round(seconds));
    }
  }, [playbackSpeed, audioFinished]);


  const setSaveTranscript = useCallback((status) => {
    setSaveTranscriptState(status);
  }, []);

  const handleRecordingComplete = useCallback((data) => {
    const { question_audio_local, defaultSelectedVoice_local } = getQuestionAudio(props.data);
    setBlobURL(data.url);
    setIsRecording(false);
    setRecordCount(100);
    setFile(data.file);
    setDuration(data.duration);
    setTrans(data.transcript);
    setQuestionAudio(question_audio_local);
    setDefaultSelectedVoice(defaultSelectedVoice_local);
  // Update latestValues immediately
    latestValues.current.file = data.file;
    latestValues.current.duration = data.duration;
    latestValues.current.blobURL = data.url;
    latestValues.current.trans = data.transcript;

    const data_to_send = {
      data: data.file,
      audio_text: data.transcript,
      duration: data.duration,
      blob_url: data.url,
    };

    if (props.testMode) {
      props.nextQuestion?.(data_to_send);
    } else {
      props.submit?.(data_to_send, true);
      props.showResult?.();
    }
  }, [props.testMode, props.nextQuestion, props.submit, props.showResult, getQuestionAudio]);

  handleRecordingCompleteRef.current = handleRecordingComplete;

  const stableHandleRecordingComplete = useCallback((data) => {
    handleRecordingCompleteRef.current(data);
  }, []);

  const changeAudio = useCallback((selected) => {
  // Don't allow change after audio finished
    if (audioFinished) return;

    if (!props.data?.question_audios) return;

    const selectedAudio = props.data.question_audios.find(
      audio => audio.label === selected.label
    );

    if (!selectedAudio) return;

    const newAudioUrl = `${s3base}/${selectedAudio.value}`;

  // Reset countdown & intervals
    if (intervalsRef.current.intervalId2) {
      clearInterval(intervalsRef.current.intervalId2);
      intervalsRef.current.intervalId2 = 0;
    }

    setQuestionAudio(newAudioUrl);
    setDefaultSelectedVoice(selected.label);
    setCurrentAudioIndex(selectedAudio);
    setAudioRetryCount(0);
  setSoundKey(prev => prev + 1);           // force Sound re-mount
    setSoundStatus(true);
    setAudioFinished(false);

  // Reset countdown (will be recalculated in onLoading)
    setSecondCount2(10);
    setSecondCount2Bckup(0);

  }, [props.data?.question_audios, audioFinished]);

  const playSound = useCallback(() => {
    const { resultMode, data } = props;

    let sound = false;
    if (!resultMode && data && soundStatus && secondCount === 0) {
      sound = true;
    }

    return sound;
  }, [props.resultMode, props.data, soundStatus, secondCount]);

  const playingQuestionSectionLabel = useCallback(() => {
    let status = "";
    if (!isRecording) {
      if (secondCount !== 0 && soundStatus === true) {
        status = `Begining in ${secondCount} seconds`;
      } else if (secondCount === 0 && blobURL === "" && audioFinished === false) {
        status = "Playing question audio...";
      } else {
    status = "Question audio finished."; // just any case.
      }
    } else {
      status = "Question audio finished.";
    }
    return status;
  }, [isRecording, secondCount, soundStatus, blobURL, audioFinished]);

  const showSkipAudioWaitButton = useCallback(() => {
    let show_button = false;

    if (secondCount !== 0 && blobURL === "" && !isRecording) {
      show_button = true;
    }

    return show_button;
  }, [secondCount, blobURL, isRecording]);

  const recordingLabel = useCallback(() => {
    let label = "";
    if (secondCount2Bckup > 0) {
      if (blobURL === "" && isRecording) {
        label = "Recording audio...";
      } else if (secondCount === 0 && secondCount2 > 0) {
        label = `Recording starts in ${secondCount2} seconds...`;
      } else if (blobURL !== "" && !isRecording) {
        label = "Recording Finished.";
      }
    }
    return label;
  }, [secondCount, secondCount2, blobURL, isRecording, secondCount2Bckup]);

  const showStopRecordingButton = useCallback(() => {
    let button = false;

    const { testMode } = props;

    if (!testMode && isRecording) {
      button = true;
    }

    return button;
  }, [props.testMode, isRecording]);

  const showRecordedAudio = useCallback(() => {
    const { testMode } = props;

    let recorded_audio = false;
    if (blobURL && !testMode) {
      recorded_audio = true;
    }

    return recorded_audio;
  }, [blobURL, props.testMode]);

  const skipAudio = useCallback(() => {
    const { testMode } = props;

    let button = false;
    if (!testMode && soundStatus === true && secondCount === 0) {
      button = true;
    }

    return button;
  }, [props.testMode, soundStatus, secondCount]);

  const playBeep = useCallback(() => {
    const { resultMode } = props;

    let beep = false;
    if (!resultMode && beepStatus) {
      beep = true;
    }
    return beep;
  }, [props.resultMode, beepStatus]);

  if (isBlocked) {
    return <NoMicrophone />;
  }

    if (showMobileStartButton && !mobileStarted) {
    return (
      <div className="pmw-tabs-section d-flex align-items-center justify-content-center">
        <div className="ios-start-container text-center" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '200px'
        }}>
          <button
            className="btn btn-primary btn-lg"
            onClick={handleMobileStart}
            disabled={isStarting}
            style={{
              padding: '15px 30px',
              fontSize: '18px',
              borderRadius: '25px',
              background: 'linear-gradient(135deg, rgb(171 248 97) 0%, rgb(104 162 75) 100%)',
              border: 'none',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              opacity: isStarting ? 0.7 : 1
            }}
          >
            <i className="fa fa-play-circle me-2"></i>
            {isStarting ? 'Starting...' : 'Start Practice'}
          </button>
          <div className="ios-instructions mt-4 p-3 bg-light rounded" style={{ maxWidth: '400px' }}>
            <small className="text-muted">
              <i className="fa fa-volume-up me-1"></i> Audio will play automatically after clicking
              <br />
              <i className="fa fa-microphone me-1 mt-1"></i> Recording will start after the question audio
            </small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {micPermitted && browserAllowed ? (
        <>
          <ReusableRecorder
            ref={recorderRef}
            mediaStream={mediaStreamRef.current}
            onRecordingComplete={stableHandleRecordingComplete}
            maxDuration={10000}
            enableSpeechRecognition={true}
            fileName="retell-lecture.mp3"
          />

          {playSound() && questionAudio && (
            isIOSDevice() && mobileStarted ? (
              <IOSAudioPlayer
                audioUrl={questionAudio}
                onPlaying={handleSongPlaying}
                onLoading={loadingSound}
                onFinishedPlaying={handleSongFinishedPlaying}
                onError={handleAudioError}
                volume={volume}
                playbackRate={playbackSpeed}
                soundKey={soundKey}
                unlockedAudio={unlockedAudioRef.current}
              />
            ) : (
              <Sound
                url={questionAudio}
                playStatus={Sound.status.PLAYING}
                onPlaying={handleSongPlaying}
                onLoading={loadingSound}
                onFinishedPlaying={handleSongFinishedPlaying}
                onError={handleAudioError}
                volume={volume}
                playbackRate={playbackSpeed}
                key={soundKey}
              />
            )
            )}

          {!isIOSDevice() && playBeep() && (
            <Sound
              url={Beep}
              playStatus={Sound.status.PLAYING}
              onFinishedPlaying={handleBeepFinishedPlaying}
              onError={handleBeepFinishedPlaying}
            />
          )}

      <div className="question_section_height pmw-tabs-section d-flex align-items-start pmw-outer-wrapper recorded-wrapper challenge-content-wrapper visibility-visible m-0" id="retell-lec"
      // style={{
      //   minHeight: "220px", height: "100%"
      // }}
      >
            <div className="recorded-section h-100" style={{ width: "50%" }}>
              <div className="recorded-content">
                <div className="row">
                  <div className="col-12">
                    <ul className="recorded-list">
                      <li>Status:</li>
                      <li>{playingQuestionSectionLabel()}</li>
                      {/* <li>Status: <span className="text-black">{playingQuestionSectionLabel()}</span></li> */}
            {/* {secondCount === 0 && !isRecording && audioDuration > 0 && audioSecondsLeft > 0 && (
            <li>
            Audio ends in: {audioSecondsLeft}s 
            </li>
            )} */}
                    </ul>
                  </div>
          {/*<div className="col-2"><span className="volume-label">Volume</span></div>*/}
                </div>

                <div className="recorded-audio-wrapper" style={{ marginTop: "50px" }}>
                  <div className="row">
                    <div className="recorded-audio-outer">
                      <ProgressBar now={soundPer} striped />

                      <div className="volume-control-wrapper">
                        <span className="volume-icon">
                          <i className="fa fa-volume-up" aria-hidden="true"></i>
                        </span>
                        <div className="re-volume-wrapper">
                          <input
                            type="range"
                            placeholder="Volume"
                            className="volume-control"
                            min="0"
                            max="100"
                            step="1"
                            defaultValue={volume}
                            onInput={changeRange}
                            style={{
                              "--progress": `${(volume / 100) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="mt-3 clearfix">
                        <div className="pull-left" id="retell-playback">
                          <PlaybackSpeedBtns
                            changePlaybackSpeed={changePlaybackSpeed}
                            playbackSpeed={playbackSpeed}
                            audioFinished={audioFinished}
                          />
                        </div>
                        {defaultSelectedVoice !== null && (
                          <div className="pull-left mx-2">
                            <SelectVoiceDropDown
                answer = {props.answer}
                sound = {soundStatus}
                              voices={props.voices}
                              selected={defaultSelectedVoice}
                              changeAudio={changeAudio}
                            />
                          </div>
                        )}
                        {showSkipAudioWaitButton() && (
                          <div className="btn-outer skip-btn-wrapper retell-skip pull-right m-0">
                            <button
                              type="button"
                              className="outline-btn skip-btn-new text-center visibility-visible"
                              onClick={skip}
                            >
                              Skip
                            </button>
                          </div>
                        )}
            {/* {skipAudio() && (
              <div className="btn-outer skip-btn-wrapper retell-skip pull-right">
              <button
                className="outline-btn skip-btn-new text-center visibility-visible"
                onClick={stopAudio}
              >
                Stop Audio
              </button>
              </div>
            )} */}

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="recorded-section h-100 position-sticky" style={{ width: "50%" }}>
              <div className="row">
                <div className="w-100">
          {/* <div> */}
                  <div className="recorded-content">
                    <ul className="recorded-list">
                      {/* <li>Status:</li>
                      <li>{recordingLabel()}</li> */}
                      <li>Status:<span className="text-black"> {recordingLabel()}</span></li>
            {isRecording && (
    <li>
      Recording: {LIMIT - recordCount}s
    </li>
  )}
                    </ul>
                    <div className="recorded-audio-wrapper">
                      {showRecordedAudio() ? (
                        <div className="mic-waveform-wrapper" style={{ marginTop: "37px" }}>
                          <RecordedWaveform
                            testMode={false}
                            blobURL={blobURL}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="mic-waveform-wrapper d-flex gap-2" id="retell-micwave">
                            {recordCount === 0 ? (
              //   <img
              //   src="/images/waveform.png"
              //   alt="Waveform"
              //   />
                              <div className="waveform" style={{ marginTop: "48px" }}>
                                {Array.from({ length: 100 }).map((_, i) => (
                                  <div key={i} className="wave-bar" />
                                ))}
                              </div>
                            ) : (
                              <MicWaveform
                // progressValue={recordCount * 2.5}
                                progressValue={recordCount * 12.5}
                              />
                            )}
                            {showStopRecordingButton() && (
                              <div className="btn-outer">
                                <button
                                  type="button"
                                  className="outline-btn skip-btn-new text-center mt-2 visibility-visible"
                                  onClick={stop}
                                >
                                  Stop
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="pmw-tabs h-100">
          {micPermitted === false && (
            <div className="alert alert-danger">
              No Microphone permissions granted. Please grant microphone
              permissions and reload the page.
            </div>
          )}

          {browserAllowed === false && (
            <div className="alert alert-danger">
              The browser you are using is not supported. Please use Google
              Chrome.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default RepeatSentece;
 
